import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';
import { AppPicker } from './AppPicker.js';
import { RecordPicker } from '../../components/RecordPicker.js';
import { personLabel } from '../../components/ApprovalLevels.js';

interface OwnerOption {
  readonly userId: string;
  readonly displayName: string;
  readonly employeeCode: string | null;
}
import { Button } from '../../../../shared/components/index.js';

interface GovernancePosture {
  readonly applicationId: string;
  readonly appCode: string;
  readonly appName: string;
  readonly publishScope: string;
  readonly governanceOwnerId: string | null;
  readonly lifecycleStatus: string;
  readonly governanceEnforced: boolean;
  readonly requiresGovernance: boolean;
  readonly compliant: boolean;
  readonly reasons: readonly string[];
}

const SCOPES = ['PRIVATE', 'DEPARTMENT', 'TENANT', 'MARKETPLACE'];

/**
 * M40 — Governance Dashboard (§20). The compliance surface over the application
 * governance posture. Inspect whether an app satisfies its governance
 * obligations (ownership, publish scope, approval — BR-01 / BR-07), then assign
 * an owner or change the publish scope. Viewing needs GOVERNANCE_VIEW; changes
 * need GOVERNANCE_MANAGE.
 */
export function GovernanceDashboard(): ReactNode {
  const { token } = useAuth();
  const { t } = useTranslation();
  const [applicationId, setApplicationId] = useState('');
  const [query, setQuery] = useState('');
  const [posture, setPosture] = useState<GovernancePosture | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(0);

  const [ownerId, setOwnerId] = useState('');
  const [publishScope, setPublishScope] = useState('PRIVATE');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!query) { setPosture(null); return; }
    let alive = true;
    setLoading(true);
    apiRequest<{ data: GovernancePosture }>(`/v1/app-platform/governance/${query}`, { token })
      .then((r) => { if (alive) { setPosture(r.data); setPublishScope(r.data.publishScope); setError(null); } })
      .catch((e) => { if (alive) { setPosture(null); setError((e as Error).message); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, query, reload]);

  const load = (e: FormEvent): void => {
    e.preventDefault();
    setNotice(null);
    setActionError(null);
    setQuery(applicationId.trim());
  };

  const setOwner = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!posture) return;
    if (!ownerId.trim()) { setActionError(t('m40.governance.ownerIdRequired')); return; }
    try {
      await apiRequest(`/v1/app-platform/governance/${posture.applicationId}/owner`, {
        method: 'PUT',
        body: { governanceOwnerId: ownerId.trim() },
        token,
      });
      setNotice(t('m40.governance.ownerUpdated'));
      setOwnerId('');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.governance.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  const setScope = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!posture) return;
    try {
      await apiRequest(`/v1/app-platform/governance/${posture.applicationId}/publish-scope`, {
        method: 'PUT',
        body: { publishScope },
        token,
      });
      setNotice(t('m40.governance.scopeUpdated'));
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError(t('m40.governance.notAuth'));
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="governance-dashboard">
      <h1>{t('m40.governance.title')}</h1>

      <section aria-label="governance-lookup">
        <form aria-label="lookup-form" onSubmit={load}>
          <AppPicker label={t('m40.common.applicationId')} value={applicationId} onPick={setApplicationId} />
          <Button variant="primary" type="submit" aria-label="submit-lookup">{t('m40.governance.loadPosture')}</Button>
        </form>
      </section>

      {error && <p role="alert" aria-label="governance-error">{error}</p>}
      {loading && <p>{t('m40.common.loading')}</p>}

      {posture && (
        <>
          <section aria-label="governance-posture">
            <h2>{posture.appCode} — {posture.appName}</h2>
            <ul>
              <li aria-label="posture-lifecycle">{t('m40.governance.lifecycleLine', { value: posture.lifecycleStatus })}</li>
              <li aria-label="posture-scope">{t('m40.governance.scopeLine', { value: posture.publishScope })}</li>
              <li aria-label="posture-owner">{t('m40.governance.ownerLine', { value: posture.governanceOwnerId ?? t('m40.governance.unassigned') })}</li>
              <li aria-label="posture-required">{t('m40.governance.requiredLine', { value: posture.requiresGovernance ? t('m40.common.yes') : t('m40.common.no') })}</li>
              <li aria-label="posture-enforced">{t('m40.governance.enforcedLine', { value: posture.governanceEnforced ? t('m40.common.yes') : t('m40.common.no') })}</li>
              <li aria-label="posture-compliant">{t('m40.governance.compliantLine', { value: posture.compliant ? t('m40.common.yes') : t('m40.common.no') })}</li>
            </ul>
            {posture.reasons.length > 0 && (
              <ul aria-label="posture-reasons">
                {posture.reasons.map((reason) => <li key={reason}>{reason}</li>)}
              </ul>
            )}
          </section>

          <section aria-label="governance-owner">
            <h2>{t('m40.governance.assignOwner')}</h2>
            <form aria-label="owner-form" onSubmit={(e) => void setOwner(e)}>
              <RecordPicker<OwnerOption>
                endpoint="/v1/users?limit=200"
                idField="userId"
                label={t('m40.governance.ownerId')}
                testId="field-owner-id"
                value={ownerId}
                optionLabel={(u) => personLabel(u.displayName, u.employeeCode) ?? u.displayName}
                onPick={setOwnerId}
              />
              <Button variant="primary" type="submit" aria-label="submit-owner">{t('m40.governance.setOwner')}</Button>
            </form>
          </section>

          <section aria-label="governance-scope">
            <h2>{t('m40.governance.publishScopeHeading')}</h2>
            <form aria-label="scope-form" onSubmit={(e) => void setScope(e)}>
              <label>{t('m40.common.scope')}
                <select aria-label="field-publish-scope" value={publishScope} onChange={(e) => setPublishScope(e.target.value)}>
                  {SCOPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <Button variant="primary" type="submit" aria-label="submit-scope">{t('m40.governance.setScope')}</Button>
            </form>
          </section>

          {actionError && <p role="alert" aria-label="governance-action-error">{actionError}</p>}
          {notice && <p aria-label="governance-notice">{notice}</p>}
        </>
      )}
    </main>
  );
}
